import { JSX, useContext, useEffect, useRef, useState } from "react";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { Link, RouteComponentProps, Redirect, useLocation, useParams } from "react-router-dom";
import { BulkTerminationProjectResponseModel, BulkProjectTypeEnum, TerminationsWizardContextSteps, BulkProviderTerminationMethod } from "@app/terminations/import/interfaces";
import TerminationsWizardConfigureReport from "./TerminationsWizardConfigureReport";
import { TerminationsWizardContext, TerminationsWizardProvider, VolumeTerminationType } from "@app/terminations/import/TerminationsWizardContext";
import TerminationsWizardFlyout from "@app/terminations/import/TerminationsWizardFlyout";
import TerminationsWizardImportList from "@app/terminations/import/TerminationsWizardImportList";
import TerminationsWizardColumnConfiguration from "@app/terminations/import/TerminationsWizardColumnConfiguration";
import TerminationsWizardReview from "@app/terminations/import/TerminationsWizardReview";
import TerminationsWizardSelectProjects from "@app/terminations/import/TerminationsWizardSelectProjects";
import { FlyoutLink } from "@app/shared/Flyouts";
import { focusFirstInteractiveElement } from "@app/shared/utility/focusFirstInteractiveElement";
import { compact } from "lodash";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { WizardSteps } from "@app/shared/qes-standards/WizardSteps";
import { useBulkTerminationModel } from "@app/terminations/import/useBulkTerminationModel";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

// Zip is only offered from the Import route, and only when the feature flag is on - Ghost
// terminations stay county-only regardless of query string, and a disabled flag must resolve
// straight to County mode rather than merely disabling the submit button later.
export const resolveTerminationsProjectType = (
  terminationType: VolumeTerminationType,
  locationSearch: string,
  isZipVolumeTerminationEnabled: boolean
): BulkProjectTypeEnum =>
  terminationType === "import"
    && new URLSearchParams(locationSearch).get("projectType") === BulkProjectTypeEnum.Zip
    && isZipVolumeTerminationEnabled
    ? BulkProjectTypeEnum.Zip
    : BulkProjectTypeEnum.County;

const TerminationsWizard = (props: RouteComponentProps): JSX.Element => {
  const { terminationType } = useParams<{ terminationType: VolumeTerminationType }>();
  const location = useLocation();
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const projectType = resolveTerminationsProjectType(
    terminationType,
    location.search,
    enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_VOLUME_TERMINATION)
  );

  return (
    // Keyed by terminationType+projectType so a query-string-only navigation (e.g. via browser
    // back/forward, or a manually edited URL) forces a clean remount instead of leaving stale
    // selected-project/form state from the previous mode in the provider.
    <TerminationsWizardProvider key={`${terminationType}-${projectType}`} terminationType={terminationType} projectType={projectType}>
      <TerminationsWizardContent {...props} />
    </TerminationsWizardProvider>
  );
};

const TerminationsWizardContent = (props: RouteComponentProps): JSX.Element => {
  const wizardContext = useContext(TerminationsWizardContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const model = useBulkTerminationModel();
  const isColumnConfigEnabled = wizardContext.terminationType === "ghost"
    ? enabledFeatureFlags.has(Features.VOLUME_GHOST_TERMINATION_REPORT_CONFIGURATION)
    : enabledFeatureFlags.has(Features.VOLUME_ACTION_REPORT_CONFIGURATION);
  const isZip = wizardContext.projectType === BulkProjectTypeEnum.Zip;
  const isZipVolumeTerminationEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_VOLUME_TERMINATION);
  const providerTerminationService = new ProviderTerminationService();
  const wizardBodyRef = useRef<HTMLDivElement>(null);
  const [pageIsValid, setPageIsValid] = useState<boolean>(false);
  const [conflicts, setConflicts] = useState<BulkTerminationProjectResponseModel[]>([]);
  const [redirectToView, setRedirectToView] = useState(false);
  const [preConflictNumberOfSelections, setPreConflictNumberOfSelections] = useState(0);

  useEffect(() => {
    focusFirstInteractiveElement(wizardBodyRef);
  }, [wizardContext.page]);

  const attemptSubmit = async () => {
    try {
      const {
        bulkProviderTerminationId,
        bulkProviderTerminationMethod,
        comment,
        eligibleSelectedProjectIds,
        filename,
        selectedLineOfBusiness,
        terminationReason,
      } = wizardContext;

      if (
        (!bulkProviderTerminationId && bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import) ||
        (bulkProviderTerminationId && bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost) ||
        (!selectedLineOfBusiness && bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost) ||
        eligibleSelectedProjectIds.length === 0 ||
        !filename ||
        !terminationReason ||
        (terminationReason === "Other" && !comment)
      ) {
        return false;
      }

      await providerTerminationService.bulkCreateProviderTerminations(model);
      setRedirectToView(true);
      return true;
    } catch {
      return false;
    }
  };

  const getConfirmationSidePanelContent = () => {
    const { shouldSkipTerminationTimeDistanceRecalc } = wizardContext;
    return <TerminationsWizardFlyout isSkipSelected={shouldSkipTerminationTimeDistanceRecalc && !isZip} onSubmit={attemptSubmit} />;
  };

  const getHumanReadablePageNames = () => {
    const humanReadablePageNames = {
      [TerminationsWizardContextSteps.SelectProjects]: "Select Projects",
      [TerminationsWizardContextSteps.ImportList]: wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import ? "Import List" : null,
      [TerminationsWizardContextSteps.Review]: wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import ? "Review" : null,
      [TerminationsWizardContextSteps.ConfigureReport]: "Configure Report",
      [TerminationsWizardContextSteps.ColumnConfiguration]: isColumnConfigEnabled ? "Column Configuration" : null,
    };

    return compact(Object.values(humanReadablePageNames))
  }

  const validateAndAdvance = async () => {
    switch (wizardContext.page) {
      case TerminationsWizardContextSteps.SelectProjects:
        if (wizardContext.eligibleSelectedProjectIds.length > 0) {
          wizardContext.setReviewProjects(wizardContext.projects.filter((p) => wizardContext.eligibleSelectedProjectIds.includes(p.projectId.toString())));
          if (wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import) {
            wizardContext.setPage(TerminationsWizardContextSteps.ImportList);
          } else {
            wizardContext.setGhostCountsCalculated(false);
            wizardContext.setPage(TerminationsWizardContextSteps.ConfigureReport);
          }
        }
        break;
      case TerminationsWizardContextSteps.ImportList:
        if (!!wizardContext.bulkImportFormData) {
          wizardContext.setPage(TerminationsWizardContextSteps.Review);
        }
        break;
      case TerminationsWizardContextSteps.Review:
        wizardContext.setPage(TerminationsWizardContextSteps.ConfigureReport);
        break;
      case TerminationsWizardContextSteps.ConfigureReport:
        if (isColumnConfigEnabled) {
          wizardContext.setPage(TerminationsWizardContextSteps.ColumnConfiguration);
        }
        break;
    } // The other possible value, .ColumnConfiguration, has a different button at the bottom
  };

  const getCurrentStep = () => {
    if (wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import) {
      return wizardContext.page;
    } else {
      switch (wizardContext.page) {
        case TerminationsWizardContextSteps.SelectProjects:
          return 0;
        case TerminationsWizardContextSteps.ConfigureReport:
          return 1;
        case TerminationsWizardContextSteps.ColumnConfiguration:
          return 2;
        default:
          return;
      }
    }
  }

  const renderCurrentWizardStep = () => {
    switch (wizardContext.page) {
      case TerminationsWizardContextSteps.SelectProjects:
        return <TerminationsWizardSelectProjects {...props} formIsValid={setPageIsValid} />;
      case TerminationsWizardContextSteps.ImportList:
        return <TerminationsWizardImportList formIsValid={setPageIsValid} />;
      case TerminationsWizardContextSteps.Review:
        return <TerminationsWizardReview formIsValid={setPageIsValid} />;
      case TerminationsWizardContextSteps.ConfigureReport:
        return <TerminationsWizardConfigureReport formIsValid={setPageIsValid} />;
      case TerminationsWizardContextSteps.ColumnConfiguration:
        return <TerminationsWizardColumnConfiguration formIsValid={setPageIsValid} />;
    }
  };

  const goToPreviousPage = () => {
    switch (wizardContext.page) {
      case TerminationsWizardContextSteps.ImportList:
        wizardContext.setPage(TerminationsWizardContextSteps.SelectProjects);
        break;
      case TerminationsWizardContextSteps.Review:
        wizardContext.setPage(TerminationsWizardContextSteps.ImportList);
        break;
      case TerminationsWizardContextSteps.ColumnConfiguration:
        wizardContext.setPage(TerminationsWizardContextSteps.ConfigureReport);
        break;
      case TerminationsWizardContextSteps.ConfigureReport:
        if (wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Import) {
          wizardContext.setPage(TerminationsWizardContextSteps.Review);
        } else {
          wizardContext.setReviewProjects([]);
          wizardContext.setSelectedLineOfBusiness("Total");
          wizardContext.setPage(TerminationsWizardContextSteps.SelectProjects);
        }
        break;
    }
  };

  const handleUpdatePage = (newPage: number) => {
    const stepNameToPage: Record<string, TerminationsWizardContextSteps> = {
      "Column Configuration": TerminationsWizardContextSteps.ColumnConfiguration,
      "Configure Report": TerminationsWizardContextSteps.ConfigureReport,
      "Import List": TerminationsWizardContextSteps.ImportList,
      "Review": TerminationsWizardContextSteps.Review,
      "Select Projects": TerminationsWizardContextSteps.SelectProjects,
    };
    const page = stepNameToPage[getHumanReadablePageNames()[newPage]];
    if (page === undefined) return;
    wizardContext.setPage(page);
    setConflicts([]);
    setPreConflictNumberOfSelections(wizardContext.totalSelectedProjectIds.length);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [wizardContext.page]);

  useEffect(() => {
    // if we have selected projects, hit api endpoint for fetching projects
    wizardContext.providerTerminationService.getBulkTerminationProjects(false, wizardContext.projectType ?? BulkProjectTypeEnum.County).then((response) => {
      // make sure no selected projects have changed into readonly mode
      const newConflicts: BulkTerminationProjectResponseModel[] = [];
      const newSelectedProjectIds: string[] = [];
      response.forEach((r) => {
        if (wizardContext.totalSelectedProjectIds.includes(r.projectId.toString()) && r.isReadOnly) {
          newConflicts.push(r);
        } else if (wizardContext.totalSelectedProjectIds.includes(r.projectId.toString())) {
          newSelectedProjectIds.push(r.projectId.toString());
        }
      });
      // if they have, set the page back to 1 and trigger an alert banner to let the user know why they're back here, that their progress was saved, and what projects were effected
      if (newConflicts.length > 0 && newConflicts.length === wizardContext.totalSelectedProjectIds.length) {
        wizardContext.setPage(TerminationsWizardContextSteps.SelectProjects);
      }
      // pulling this into a variable because I don't know how React batches set calls, and don't want to use a state value that's changing in another set call
      const oldProjectsSelected = wizardContext.totalSelectedProjectIds.length;
      setPreConflictNumberOfSelections(oldProjectsSelected);
      if (newConflicts.length > 0) {
        wizardContext.setTotalSelectedProjectIds(newSelectedProjectIds);
        // filter the eligible project selections by the still-valid total selections
        wizardContext.setEligibleSelectedProjectIds((prevValue) => {
          const newValue = prevValue.filter((id) => newSelectedProjectIds.includes(id));
          return newValue;
        })
      }

      setConflicts(newConflicts);
    });
  }, [wizardContext.page]);

  if (redirectToView) {
    return <Redirect to="/terminations/view" push={false} />;
  }

  const getPageTitle = (): string => {
    if (wizardContext.bulkProviderTerminationMethod == BulkProviderTerminationMethod.Ghost) {
      return "Ghost Terminations";
    }
    if (isZip) {
      return "New Zip Code Terminations";
    }
    if (isZipVolumeTerminationEnabled) {
      return "New County Terminations";
    }
    return "New Terminations";
  };

  const pageTitle = getPageTitle();

  return (
    <Page documentTitle={pageTitle}>
      {/* The design for the wizard does not have breadcrumbs */}
      <PageInner className="terminations-wizard">
        <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
          <h1 className="page-title mt-0 mb-0">{pageTitle}</h1>
        </header>
        <WizardSteps currentStep={getCurrentStep()} steps={getHumanReadablePageNames()} setStep={handleUpdatePage} />
        {conflicts.length > 0 && conflicts.length < preConflictNumberOfSelections && (
          <QESAlert classNames="mt-3 mb-3" type="info" collapseTitle="The following selected projects have been changed to read only mode and unselected:">
            <ul className="mb-0">
              {conflicts.map((conflict) => <li key={conflict.projectId}>{conflict.projectName}</li>)}
            </ul>
          </QESAlert>
        )}
        {conflicts.length > 0 && conflicts.length === preConflictNumberOfSelections && (
          <QESAlert classNames="mt-3 mb-3" type="info">
            All of the projects you selected have been changed to read only mode. No other selections made during the bulk termination setup process have been lost.
          </QESAlert>
        )}
        <hr />
        <div ref={wizardBodyRef}>
          {renderCurrentWizardStep()}
        </div>
      </PageInner>
      <WizardNavigationBar
        leftSide={(
          <Link to="/terminations/view" className="qes-button qes-button-secondary danger" data-testid="import-terminations__cancel">Cancel</Link>
        )}
        rightSide={(
          <div className="new-terms__nav-buttons">
            {wizardContext.page !== TerminationsWizardContextSteps.SelectProjects && <QESButton qesType="secondary" onClick={goToPreviousPage} qesTestId="import-terminations__previous">Previous</QESButton>}
            {(wizardContext.page === TerminationsWizardContextSteps.ColumnConfiguration
              || (wizardContext.page === TerminationsWizardContextSteps.ConfigureReport && !isColumnConfigEnabled)) ? (
              <FlyoutLink
                disabled={!pageIsValid || (isZip && !isZipVolumeTerminationEnabled)}
                content={{
                  body: getConfirmationSidePanelContent(),
                  title: "Warning"
                }}
                classNames="qes-button-primary ms-3"
                testId="import-terminations-submit"
                size="40"
              >
                Submit
              </FlyoutLink>
            ) : (
              <QESButton onClick={validateAndAdvance} disabled={!pageIsValid} qesTestId="import-terminations__next" className="ms-3">Next</QESButton>
            )}
          </div>
        )}
      />
    </Page>
  );
};

export default TerminationsWizard;
